import { DropdownSelect } from "@factorearth/component-library";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { FormGroup, FormInstance, FormTemplate, Project, UserForResolver } from "db/definitions";
import React, { useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useSingleton";
import buildFilterOptions from "../../../utils/buildFilterOptions";
import CreateNewInstance from "../../CreateNewInstance/CreateNewInstance";
import styles from "./FormGroupOptions.module.scss";
import FormGroupOptionsItem from "./FormGroupOptionsItem/FormGroupOptionsItem";

interface Props {
	deleteFormInstance: (instance: FormInstance, index: number) => void;
	formGroup: FormGroup;
	formTemplates: FormTemplate[];
	handleCreateNewFormSubmit: (selection: FormTemplate) => Promise<void>;
	handleDuplicateFormInstance: (formInstance: FormInstance) => Promise<void>;
	handleInitiateRearrangeFormInstances: (dropResult: DropResult) => void;
	project: Project | null;
	setInstanceToNav: (id: string) => void;
	user: UserForResolver;
}

/**
 * Form Group Options menu
 * @param props for the component as follows
 * @param formGroup the formGroup the options is in
 * @param deleteFormInstance function to delete form instance
 * @param handleInitiateRearrangeFormInstances function to rearrange the form instances and update in cloud
 * @param formTemplates that can be used to create form instances in this form group.
 * @param setInstanceToNav to handle scrolling to a form instance in the form group
 * @param handleCreateNewFormSubmit callback method to call with the new instance data for creation
 */
function FormGroupOptions(props: Props) {
	const {
		deleteFormInstance,
		formGroup,
		formTemplates,
		handleCreateNewFormSubmit,
		handleDuplicateFormInstance,
		handleInitiateRearrangeFormInstances,
		project,
		setInstanceToNav,
		user,
	} = props;

	const color = useTheme();
	const [animationComplete, setAnimationComplete] = useState(false);
	const [templateGroupFilter, setTemplateGroupFilter] = useState("Show All Templates");
	// const [groupFilter, setGroupFilter] = useState(true);
	// const [projectFilter, setProjectFilter] = useState(false);
	const [filter, setFilter] = useState<"group" | "project" | null>(null);

	const instanceList = useMemo(() => {
		const final = [];
		if (formGroup.formInstances) {
			for (let i = 0; i < formGroup.formInstances.items.length; i++) {
				const formInstance = formGroup.formInstances.items[i];
				const html = (
					<React.Fragment key={`DraggableFormInstance-${formInstance?.id}`}>
						<FormGroupOptionsItem
							color={color}
							index={i}
							instance={formInstance}
							setInstanceToNav={setInstanceToNav}
							deleteFormInstance={deleteFormInstance}
							handleDuplicateFormInstance={handleDuplicateFormInstance}
						/>
					</React.Fragment>
				);

				final.push(html);
			}
		}
		return final;
	}, [color, deleteFormInstance, formGroup.formInstances?.items, setInstanceToNav]);

	function handleDragEnd(result: DropResult) {
		handleInitiateRearrangeFormInstances(result);
	}

	function handleGroupFilter(templateGroupOption: unknown) {
		const newTemplateGroupOption = templateGroupOption as { value: string; label: string };
		const { value } = newTemplateGroupOption;
		setTemplateGroupFilter(value);
	}

	const filterOptions = useMemo(() => buildFilterOptions(formTemplates), [formTemplates]);

	return (
		<div
			className={`${styles.container}${animationComplete ? ` ${styles.containerAnimationComplete}` : ""}`}
			style={{ background: color.background.primary }}
			onAnimationEnd={() => setAnimationComplete(true)}
			id="formManagerMenu">
			<div className={styles.filterBox}>
				<DropdownSelect
					name="filter"
					id="filter"
					data-test="form-group-type"
					onChange={handleGroupFilter}
					value={{ label: templateGroupFilter, value: templateGroupFilter }}
					className={styles.select}
					options={filterOptions}
					isSearchable={true}
					menuPlacement="auto"
					minMenuHeight={300}
					classNamePrefix={"react-select"}
					placeholder={"Filter by template group..."}
					// @ts-ignore
					colorPalette={color}
				/>

				<div className={styles.checkboxAndLabel}>
					<input
						type="radio"
						name="template-filter"
						checked={filter === "project"}
						onChange={() => setFilter("project")}
						className={styles.filterCheckbox}
					/>
					<label style={{ color: color.text.primary }} className={styles.filterLabel}>
						{project?.name} templates only
					</label>
				</div>
				<div className={styles.checkboxAndLabel}>
					<input
						type="radio"
						name="template-filter"
						checked={filter === "group"}
						onChange={() => setFilter("group")}
						className={styles.filterCheckbox}
					/>
					<label style={{ color: color.text.primary }} className={styles.filterLabel}>
						{user.group} templates only
					</label>
				</div>

				{/* </div> */}

				<CreateNewInstance
					color={color}
					formTemplates={formTemplates}
					submitCreate={handleCreateNewFormSubmit}
					templateGroupFilter={templateGroupFilter}
					filter={filter}
					user={user}
					project={project}
				/>
			</div>
			<DragDropContext key={formGroup?.id} onDragEnd={(e) => handleDragEnd(e)}>
				<Droppable key={`${formGroup?.id}-droppable`} direction="vertical" droppableId={`${formGroup?.id}-droppable`}>
					{(provided, snapshot) => (
						<>
							<div ref={provided.innerRef} {...provided.droppableProps} className={styles.listContainer}>
								{instanceList}
								{provided.placeholder}
							</div>
						</>
					)}
				</Droppable>
			</DragDropContext>
		</div>
	);
}

export default FormGroupOptions;
